/**
 * Scenario management store backed by GraphQL.
 *
 * Scenarios are fetched via `listScenarios` and mutated via
 * `createScenario` / `updateScenario` / `deleteScenario`.
 * The active scenario ID is a shared reactive variable so all
 * consumers see the same value.
 *
 * Uses optimistic cache updates so pills appear immediately
 * without waiting for network round-trips.
 * @module features/squad-planning/stores/scenarioStore
 */

import { useCallback } from "react";

import { makeVar, useReactiveVar } from "@apollo/client";

import {
  useSquadPlanningListScenariosQuery,
  useSquadPlanningCreateScenarioMutation,
  useSquadPlanningUpdateScenarioMutation,
  useSquadPlanningDeleteScenarioMutation,
  SquadPlanningListScenariosDocument,
  SquadPlanningGetScenarioDocument,
  SquadPlanningGetSquadDepthByPositionDocument,
  SquadPlanningGetSquadKpIsDocument,
  SquadPlanningGetSquadPlayersByPositionDocument,
  CompliancePlanStatusDocument,
  ComplianceLayersDocument,
  type SquadPlanningListScenariosQuery,
  type ScenarioActionInput,
} from "@/generated/graphql";
import { resolveScenarioAccessDenial } from "@/features/squad-planning/utils/resolveScenarioAccessDenial";
import { transitionsOnlyVar } from "./mobileToolbarStore";
import { syncScenarioEntryScopeToActive } from "./scenarioEntryScopeStore";
import { clearPlayerOverrides } from "./playerOverridesStore";

/** Minimal data for an externally signed player */
export interface SignedPlayerData {
  /** Display name (known name or first+last) */
  readonly displayName: string;
  /** Current team name */
  readonly teamName: string | null;
  /** Comma-separated position abbreviations */
  readonly positions: string;
  /** Player age */
  readonly age: number | null;
  /** GPR score */
  readonly gprScore: number | null;
  /** Nationality name for flag display */
  readonly nationalityName: string | null;
}

/** Optional hooks into `updateScenario` (SE-8347). */
export interface UpdateScenarioOptions {
  /**
   * Called once the mutation itself has succeeded, before its refetches
   * settle. A caller that rolls back on failure must not roll back a write
   * the server already holds because a refetch afterwards rejected.
   */
  readonly onMutated?: () => void;
}

/** Shape of a scenario from the backend list query. */
export interface BackendScenario {
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** Owner, or null for a legacy scenario nobody has claimed (SE-8340). */
  readonly ownerUserId?: string | null;
  /** True when the scenario predates ownership and nobody has claimed it. */
  readonly isLegacyNoOwner: boolean;
  /** Revision the client read; sent back as `expectedRevision` on a claim. */
  readonly revision: number;
}

/** Shared reactive variable for the active scenario ID. */
const activeScenarioIdVar = makeVar<string | null>(null);

/**
 * The single writer of the active scenario id (SE-8345, SE-8444). Every
 * transition — a pill press, a create, a delete falling back to Current
 * Squad, the deferred confirm-discard switch — goes through here so the
 * session entry scope follows the active scenario (R07 S8) AND
 * `clearPlayerOverrides` always runs alongside it: a manual-salary edit
 * optimistically applied in one view must never carry over into the next,
 * since the server data underneath is already correctly isolated per
 * scenario and the override's only job is to bridge the gap before the new
 * view's own fetch resolves.
 * @param id - The scenario to activate, or null for Current Squad.
 */
export function setActiveScenarioId(id: string | null): void {
  const previous = activeScenarioIdVar();
  activeScenarioIdVar(id);
  syncScenarioEntryScopeToActive(previous, id);
  clearPlayerOverrides();
}

/**
 * SE-8504 — the active scenario id alone, without `useScenarios`'
 * `listScenarios` query. For a leaf component (one instance per table row or
 * cell, e.g. `EditableMoneyCellContainer`) that needs to scope a write to the
 * active scenario but has no other reason to hold the full scenario list.
 * @returns The active scenario id, or null for Current Squad.
 */
export function useActiveScenarioId(): string | null {
  return useReactiveVar(activeScenarioIdVar);
}

/**
 * Cross-component rename request (SE-5338). The Sandbox banner's Rename
 * button lives outside the pill bar, but inline rename is chip-owned —
 * setting this var asks ScenarioPillBarContainer to enter rename mode for
 * the given scenario, after which it resets the var to null.
 */
export const scenarioRenameRequestVar = makeVar<string | null>(null);

/**
 * Requests inline rename of a scenario chip from outside the pill bar.
 * @param id - Scenario ID to start renaming
 */
export function requestScenarioRename(id: string): void {
  scenarioRenameRequestVar(id);
}

/**
 * Whether the create-first Create Scenario modal is open (SE-5345). Both
 * the pill bar's "+ Create new scenario" and the Actions overview
 * empty-state CTA open the single instance mounted at the SquadPlanning
 * screen by setting this var; the modal derives `isOpen` from it
 * directly (no effects involved).
 */
export const createScenarioModalVar = makeVar<boolean>(false);

/** Opens the create-first Create Scenario modal. */
export function requestCreateScenarioModal(): void {
  createScenarioModalVar(true);
}

/** Closes the create-first Create Scenario modal. */
export function dismissCreateScenarioModal(): void {
  createScenarioModalVar(false);
}

/**
 * Hook for managing scenarios via backend GraphQL operations.
 *
 * All consumers share the same `activeId` via a reactive variable,
 * ensuring scenario selection is consistent across all components.
 * @returns Scenario state and CRUD functions
 */
export function useScenarios() {
  const { data, loading, error } = useSquadPlanningListScenariosQuery({
    fetchPolicy: "cache-and-network",
  });

  const activeId = useReactiveVar(activeScenarioIdVar);
  const [createMutation] = useSquadPlanningCreateScenarioMutation();
  const [updateMutation] = useSquadPlanningUpdateScenarioMutation();
  const [deleteMutation] = useSquadPlanningDeleteScenarioMutation();

  const scenarios: readonly BackendScenario[] = data?.listScenarios ?? [];
  const activeScenario = scenarios.find(s => s.id === activeId) ?? null;

  // SE-6820: `data?.listScenarios ?? []` collapses three different situations
  // into one empty array — "you are not allowed", "still loading", and "you
  // genuinely have none". A MEMBER hitting the role guard therefore saw an
  // empty scenario strip that reads as "no scenarios yet". Keep the refusal
  // distinguishable so consumers can say what is actually true.
  const accessDeniedMessage = resolveScenarioAccessDenial(error);

  // SE-8409 AC7: `duplicateFromScenarioId` tells the backend to copy the
  // SOURCE scenario's own isolated manual-salary snapshot into the new
  // scenario, instead of falling back to the Current Squad's live one.
  // Passed by SandboxBannerContainer's Duplicate action and
  // CreateScenarioModalContainer's "Copy from existing" flow.
  const createScenario = useCallback(
    async (
      name: string | undefined,
      actions: readonly ScenarioActionInput[],
      // SE-8345: a create from another surface (scouting) must not activate
      // the new scenario over an in-flight draft; the caller hands the switch
      // to the confirm-discard flow instead. SE-8609: a create seeded from a
      // selection asks the backend to drop the SIGNs it cannot hold (rostered
      // or inaccessible players) instead of refusing the whole create.
      options: {
        readonly activate?: boolean;
        readonly skipIneligibleSignings?: boolean;
      } = {},
      // SE-8409 AC7: copies the SOURCE scenario's own isolated manual-salary
      // snapshot into the new scenario, instead of falling back to the
      // Current Squad's live one. See the module-level comment above.
      duplicateFromScenarioId?: string
    ): Promise<string | null> => {
      const result = await createMutation({
        variables: {
          input: {
            name,
            actions: [...actions],
            duplicateFromScenarioId,
            // Absent unless opted in, so every other create sends what it did.
            ...(options.skipIneligibleSignings === true
              ? { skipIneligibleSignings: true }
              : {}),
          },
        },
        update(cache, { data: mutationData }) {
          if (mutationData == null) return;
          const existing = cache.readQuery<SquadPlanningListScenariosQuery>({
            query: SquadPlanningListScenariosDocument,
          });
          cache.writeQuery<SquadPlanningListScenariosQuery>({
            query: SquadPlanningListScenariosDocument,
            data: {
              listScenarios: [
                ...(existing?.listScenarios ?? []),
                mutationData.createScenario,
              ],
            },
          });
        },
      });
      const newId = result.data?.createScenario.id ?? null;
      if (newId != null && options.activate !== false) {
        setActiveScenarioId(newId);
      }
      return newId;
    },
    [createMutation]
  );

  const updateScenario = useCallback(
    async (
      scenarioId: string,
      name?: string,
      actions?: readonly ScenarioActionInput[],
      options?: UpdateScenarioOptions
    ): Promise<void> => {
      await updateMutation({
        variables: {
          input: {
            scenarioId,
            name,
            actions: actions != null ? [...actions] : undefined,
          },
        },
        // Apollo runs `update` with the mutation's own result, before the
        // refetches below: the one moment a caller can learn the write
        // landed even if a refetch then rejects the whole promise (SE-8347).
        update: () => options?.onMutated?.(),
        refetchQueries: [
          { query: SquadPlanningListScenariosDocument },
          {
            query: SquadPlanningGetScenarioDocument,
            variables: { scenarioId },
          },
          {
            query: SquadPlanningGetSquadKpIsDocument,
            variables: { scenarioId },
          },
          {
            query: SquadPlanningGetSquadPlayersByPositionDocument,
            variables: { scenarioId },
          },
          {
            query: SquadPlanningGetSquadDepthByPositionDocument,
            variables: { scenarioId },
          },
          // The Plan Status tile (SE-7003) is keyed by scenarioId; a save
          // that changes the actions must re-evaluate THAT entry or the tile
          // keeps reporting the verdict of the actions just replaced.
          {
            query: CompliancePlanStatusDocument,
            variables: { scenarioId },
          },
          // SE-8547: the compliance layer board reads the same evaluations,
          // and `layers` now merges rather than being replaced, so it no
          // longer refreshes as a side effect of the entry above. A bare
          // document, unlike the entries above: it refetches only a board
          // that is MOUNTED, with its own `scenarioId`, instead of firing this
          // expensive read after every Squad Planning save.
          ComplianceLayersDocument,
        ],
        awaitRefetchQueries: true,
      });
    },
    [updateMutation]
  );

  const deleteScenario = useCallback(
    async (id: string): Promise<void> => {
      const wasActive = activeScenarioIdVar() === id;
      await deleteMutation({
        variables: { scenarioId: id },
        update(cache) {
          const existing = cache.readQuery<SquadPlanningListScenariosQuery>({
            query: SquadPlanningListScenariosDocument,
          });
          if (existing == null) return;
          cache.writeQuery<SquadPlanningListScenariosQuery>({
            query: SquadPlanningListScenariosDocument,
            data: {
              listScenarios: existing.listScenarios.filter(s => s.id !== id),
            },
          });
        },
      });
      if (wasActive) {
        setActiveScenarioId(null);
        transitionsOnlyVar(false);
      }
    },
    [deleteMutation]
  );

  const renameScenario = useCallback(
    async (id: string, name: string): Promise<void> => {
      await updateMutation({
        variables: { input: { scenarioId: id, name } },
        optimisticResponse: {
          updateScenario: {
            __typename: "ScenarioModel",
            id,
            name,
            updatedAt: new Date().toISOString(),
          },
        },
        update(cache, { data: mutationData }) {
          if (mutationData == null) return;
          const existing = cache.readQuery<SquadPlanningListScenariosQuery>({
            query: SquadPlanningListScenariosDocument,
          });
          if (existing == null) return;
          cache.writeQuery<SquadPlanningListScenariosQuery>({
            query: SquadPlanningListScenariosDocument,
            data: {
              listScenarios: existing.listScenarios.map(s =>
                s.id === id
                  ? {
                      ...s,
                      name,
                      updatedAt: mutationData.updateScenario.updatedAt,
                    }
                  : s
              ),
            },
          });
        },
      });
    },
    [updateMutation]
  );

  const setActiveScenario = useCallback((id: string | null) => {
    setActiveScenarioId(id);
  }, []);

  return {
    scenarios,
    activeId,
    activeScenario,
    isCurrentView: activeId === null,
    loading,
    /**
     * The role guard's own message when the scenario read was REFUSED, else
     * null. Non-null means the empty `scenarios` array is a permission
     * boundary, not an empty account (SE-6820).
     */
    accessDeniedMessage,
    createScenario,
    updateScenario,
    deleteScenario,
    renameScenario,
    setActiveScenario,
  };
}
